// ✅ UPDATED App.js
// - Adds DEV-only DevMenuScreen so YOU (developer) can jump to Taxi/Driver flows
// - Adds Driver screens
// - Keeps existing deep linking for taxi paths like /ce508no (NO expo-linking needed)

import React, { useEffect, useRef, useState, createContext } from "react";

import {
Platform,
View,
Modal,
StyleSheet,
Text,
TouchableOpacity,
Image,
ActivityIndicator,
} from "react-native";

import {
useFonts,
Montserrat_400Regular,
Montserrat_600SemiBold,
Montserrat_700Bold,
} from "@expo-google-fonts/montserrat";

import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import { StatusBar as ExpoStatusBar } from "expo-status-bar";

import "./src/global.css";

// ✅ i18n provider
import { LanguageProvider } from "./src/i18n";

// ✅ screens (Taxi)
import HomeScreen from "./src/pages/HomeScreen";
import OfficialControlScreen from "./src/pages/OfficialControlScreen";

// ✅ screens (Driver)
import DriverHomeScreen from "./src/pages/DriverHomeScreen";
import DriverOfficialControlScreen from "./src/pages/DriverOfficialControlScreen";

// ✅ DEV screen (only for you)
import DevMenuScreen from "./src/pages/DevMenuScreen";

export const AppContext = createContext();
const Stack = createStackNavigator();

const COLORS = {
primary: "#024A59",
secondary: "#D99178",
text: "#0b1a1f",
white: "#ffffff",
};

// Loading component
function LoadingScreen() {
return (
<View style={styles.loadingContainer}>
<ActivityIndicator size="large" color={COLORS.primary} />
<Text style={styles.loadingText}>Loading...</Text>
</View>
);
}

// Button component
const Button = ({ title, onPress, variant = "primary", style }) => {
const buttonStyle = [
styles.button,
variant === "secondary" ? styles.buttonSecondary : styles.buttonPrimary,
style,
];

const textStyle = [
styles.buttonText,
variant === "secondary" ? styles.buttonTextSecondary : styles.buttonTextPrimary,
];

return (
<TouchableOpacity style={buttonStyle} onPress={onPress}>
<Text style={textStyle}>{title}</Text>
</TouchableOpacity>
);
};

function InstallPrompt({ visible, onClose, deferredEvent }) {
const isIOS =
Platform.OS === "ios" ||
(Platform.OS === "web" && /iPad|iPhone|iPod/.test(navigator.userAgent));
const canPrompt = !!deferredEvent;

return (
<Modal animationType="fade" transparent visible={visible}>
<View style={styles.modalBackdrop}>
<View style={styles.modalSheet}>
<View style={{ flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 6 }}>
<Image
source={require("./assets/icons/yellowtaxi.png")}
style={{ width: 35, height: 35, borderRadius: 6 }}
/>
<Text style={styles.modalTitle}>Install the app</Text>
</View>

{isIOS ? (
<Text style={styles.modalText}>
On iPhone/iPad: tap the Share button in Safari, then choose “Add to Home Screen”.
</Text>
) : (
<View>
<Text style={styles.modalText}>
{canPrompt
? "Install this app for faster access."
: "To install: open your browser menu and choose “Install app” / “Add to Home screen”."}
</Text>

<Button
title={canPrompt ? "Install now" : "OK"}
onPress={() => {
if (deferredEvent) deferredEvent.prompt();
onClose();
}}
variant="secondary"
style={{ marginTop: 12 }}
/>
</View>
)}

<TouchableOpacity onPress={onClose} style={{ marginTop: 16, alignItems: "center" }}>
<Text style={[styles.modalText, { color: COLORS.primary, fontWeight: "600" }]}>
Later
</Text>
</TouchableOpacity>
</View>
</View>
</Modal>
);
}

export default function App() {
const [fontsLoaded] = useFonts({
Montserrat_400Regular,
Montserrat_600SemiBold,
Montserrat_700Bold,
});

const [showPrompt, setShowPrompt] = useState(false);
const deferredPromptRef = useRef(null);

useEffect(() => {
if (Platform.OS === "web") {
const isStandalone =
window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;

let timer;

const onBIP = (e) => {
e.preventDefault();
deferredPromptRef.current = e;
if (!isStandalone) setShowPrompt(true);
};

const showSoon = () => {
if (!isStandalone) setShowPrompt(true);
};

window.addEventListener("beforeinstallprompt", onBIP);
timer = setTimeout(showSoon, 1200);

return () => {
window.removeEventListener("beforeinstallprompt", onBIP);
if (timer) clearTimeout(timer);
};
}
}, []);

/**
* ✅ Web-safe Deep Linking config (NO expo-linking)
*
* Keep existing taxi behavior:
* - "/" -> home_screen (taxi)
* - "/ce508no" -> home_screen with route.params.ref="ce508no"
*
* Add OPTIONAL driver routes (won't affect users unless you navigate there):
* - "/driver/DRIVER-001" -> driver_home with ref
* - "/driver/DRIVER-001/official" -> driver_official_control_screen with ref
*/
const linking =
Platform.OS === "web"
? {
prefixes: [window.location.origin, "https://verify.taxi-yaounde.com"],
config: {
screens: {
// TAXI
home_screen: ":ref?",
official_control_screen: "official",

// DRIVER (optional)
driver_home: "driver/:ref",
driver_official_control_screen: "driver/:ref/official",

// DEV MENU (optional, dev only)
dev_menu: "dev",
},
},
}
: undefined;

if (!fontsLoaded) return <LoadingScreen />;

// ✅ Dev-only choice:
// - If you want to start in Dev Menu during development, set this to true.
// - In production it will always start at home_screen.
const START_IN_DEV_MENU = true; // <-- set false if you don't want it as initial in dev

const initialRouteName =
__DEV__ && START_IN_DEV_MENU ? "dev_menu" : "home_screen";

return (
<LanguageProvider>
<AppContext.Provider value={{}}>
<View style={styles.container}>
<NavigationContainer linking={linking}>
<Stack.Navigator initialRouteName={initialRouteName} screenOptions={{ headerShown: false }}>
{/* ✅ DEV ONLY */}
{__DEV__ && <Stack.Screen name="dev_menu" component={DevMenuScreen} />}

{/* ✅ TAXI */}
<Stack.Screen name="home_screen" component={HomeScreen} />
<Stack.Screen name="official_control_screen" component={OfficialControlScreen} />

{/* ✅ DRIVER */}
<Stack.Screen name="driver_home" component={DriverHomeScreen} />
<Stack.Screen
name="driver_official_control_screen"
component={DriverOfficialControlScreen}
/>
</Stack.Navigator>
</NavigationContainer>

{Platform.OS === "web" && (
<InstallPrompt
visible={showPrompt}
onClose={() => setShowPrompt(false)}
deferredEvent={deferredPromptRef.current}
/>
)}
</View>

<ExpoStatusBar style="auto" />
</AppContext.Provider>
</LanguageProvider>
);
}

const styles = StyleSheet.create({
container: { flex: 1, backgroundColor: "white" },

loadingContainer: {
flex: 1,
justifyContent: "center",
alignItems: "center",
backgroundColor: "white",
},
loadingText: {
marginTop: 16,
fontSize: 16,
color: COLORS.primary,
fontFamily: "Montserrat_400Regular",
},

button: { borderRadius: 12, paddingVertical: 14, alignItems: "center" },
buttonPrimary: { backgroundColor: COLORS.primary },
buttonSecondary: { backgroundColor: COLORS.secondary },
buttonText: { fontFamily: "Montserrat_700Bold", fontSize: 16 },
buttonTextPrimary: { color: COLORS.white },
buttonTextSecondary: { color: COLORS.text },

modalBackdrop: {
...Platform.select({ web: { position: "fixed" }, default: { position: "absolute" } }),
left: 0,
right: 0,
top: 0,
bottom: 0,
backgroundColor: "rgba(0,0,0,0.45)",
justifyContent: "center",
alignItems: "center",
padding: 16,
},
modalSheet: {
width: "100%",
maxWidth: 460,
backgroundColor: "white",
borderRadius: 16,
padding: 16,
},
modalTitle: { fontFamily: "Montserrat_700Bold", fontSize: 18, color: COLORS.text },
modalText: { fontFamily: "Montserrat_400Regular", color: COLORS.text },
});